/**
 * Tags outgoing API requests with the client app and whether the user is idle,
 * so the backend moves "last seen" only on user-initiated requests.
 *
 * Idle means the tab is hidden, or there has been no interaction for
 * IDLE_AFTER_MS. No header means active, so untagged clients still count.
 */

export const COMMANDER_CLIENT_HEADER = 'X-Commander-Client';
export const COMMANDER_ACTIVITY_HEADER = 'X-Commander-Activity';

const IDLE_AFTER_MS = 5 * 60 * 1000;
const POINTERMOVE_THROTTLE_MS = 1000;

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/** Where interaction events come from and how the tab's state is read. */
export interface ActivityEnvironment {
  addEventListener(type: string, listener: () => void, options?: AddEventListenerOptions): void;
  isHidden(): boolean;
  now(): number;
}

export interface ActivityTracker {
  isIdle(): boolean;
}

/** Listeners attach on the first `isIdle()` call, once per tracker. */
export function createActivityTracker(
  env: ActivityEnvironment,
  idleAfterMs = IDLE_AFTER_MS
): ActivityTracker {
  let started = false;
  let lastInteractionAt = 0;

  const markInteraction = () => {
    lastInteractionAt = env.now();
  };

  const markPointerMove = () => {
    if (env.now() - lastInteractionAt >= POINTERMOVE_THROTTLE_MS) markInteraction();
  };

  const start = () => {
    started = true;
    markInteraction();
    const passive = { passive: true, capture: true };
    for (const type of INTERACTION_EVENTS) env.addEventListener(type, markInteraction, passive);
    env.addEventListener('pointermove', markPointerMove, passive);
    // Element scroll events do not bubble; capture catches them on window.
    env.addEventListener('scroll', markInteraction, passive);
    env.addEventListener('focus', markInteraction, { passive: true });
  };

  return {
    isIdle() {
      if (!started) start();
      return env.isHidden() || env.now() - lastInteractionAt >= idleAfterMs;
    },
  };
}

let browserTracker: ActivityTracker | null = null;

function getBrowserTracker(): ActivityTracker | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  browserTracker ??= createActivityTracker({
    addEventListener: (type, listener, options) => window.addEventListener(type, listener, options),
    isHidden: () => document.hidden,
    now: () => Date.now(),
  });
  return browserTracker;
}

/** Headers every Commander product request sends alongside the user JWT. */
export function getCommanderClientHeaders(): Record<string, string> {
  const headers: Record<string, string> = { [COMMANDER_CLIENT_HEADER]: 'web' };
  if (getBrowserTracker()?.isIdle()) headers[COMMANDER_ACTIVITY_HEADER] = 'idle';
  return headers;
}
